import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';

const HINT_WIDTH = 280;
const HINT_OFFSET = 8;

/** Подсказка по клику: рендерится в body, чтобы не обрезалась скроллом таблицы */
const ClickTooltip = ({ text, children, className }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;

    const maxLeft = window.innerWidth - HINT_WIDTH - HINT_OFFSET;
    setPosition({
      top: rect.bottom + HINT_OFFSET,
      left: Math.min(Math.max(HINT_OFFSET, rect.left), Math.max(HINT_OFFSET, maxLeft)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    updatePosition();

    const handleOutsideClick = (event) => {
      if (!anchorRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return (
    <span ref={anchorRef} className="inline-flex items-center">
      <button
        type="button"
        aria-label="Подсказка"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="inline-flex items-center rounded-full p-0.5 hover:bg-gray-100 transition-colors">
        {children}
      </button>

      {open && position
        ? createPortal(
            <div
              style={{ top: position.top, left: position.left, maxWidth: HINT_WIDTH }}
              className={cn(
                `fixed z-50 w-max rounded-lg bg-white px-3 py-2
                 text-xs font-normal normal-case leading-snug text-gray-700
                 shadow-lg ring-1 ring-gray-200 pointer-events-none`,
                className
              )}>
              {text}
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

const Tooltip = ({ text, children, className, trigger = 'hover' }) => {
  if (trigger === 'click') {
    return (
      <ClickTooltip text={text} className={className}>
        {children}
      </ClickTooltip>
    );
  }

  return (
    <div className="relative group inline-block">
      {children}
      <div
        className={cn(
          `absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block
           shadow bg-white text-orange-500 text-xs rounded px-2 py-1
           whitespace-nowrap select-none pointer-events-none`,
          className
        )}>
        {text}
      </div>
    </div>
  );
};

export default Tooltip;
